import React from 'react';

import {DataFrame} from 'data-frames/DataFrameTypes';
import resolveDataFrame from 'data-frames/util/resolveDataFrame';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {VISUALIZATION_RESOLUTION_CONFIG} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Heatmap, {HeatmapPoint} from 'ui-library/charting/Heatmap';

import convertHeatmapProps from './prop-converters/convertHeatmapProps';

type HeatmapParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationHeatmapParametersNode'
>;

// For performance, a Heatmap cannot be larger than a 100x100=10,000 heatmap
export const MAX_HEATMAP_SIZE = 10000;

export function isHeatmapTooLarge<DataPoint extends HeatmapPoint>(points: Array<DataPoint>): boolean {
  const uniqueXCount = new Set(points.map((point) => point.x)).size;
  const uniqueYCount = new Set(points.map((point) => point.y)).size;
  return uniqueXCount * uniqueYCount > MAX_HEATMAP_SIZE;
}

export interface HeatmapViewerProps {
  dataFrame: DataFrame;
  parameters: HeatmapParameters;
}

export default function HeatmapViewer({dataFrame, parameters}: HeatmapViewerProps): JSX.Element {
  const resolvedDataFrame = resolveDataFrame(dataFrame, VISUALIZATION_RESOLUTION_CONFIG);
  const conversionOutput = convertHeatmapProps(resolvedDataFrame, parameters);

  if (!conversionOutput.valid) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={conversionOutput.message} />;
  } else if (isHeatmapTooLarge(conversionOutput.props.data.points)) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="The heatmap is too large to display. Heatmaps can display up to 10,000 bins."
      />
    );
  } else {
    return <Heatmap {...conversionOutput.props} />;
  }
}
